import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { useScrollToBottom } from '../../hooks'

import { getTermsDetail } from '../../api/terms'

import { ModalWrap } from '../modal/ModalWrap'
import { CenterWrap } from '../common'
import { Content } from '../container/Content'
import { Button } from '../button'
import type { ButtonProps } from '../button/Button'
import { Footer } from '../footer'
import { Header } from '../header'
import { Spinner } from '../spinner/Spinner'
import { ArtipioContents, ArtipioContentItem } from '../contents'

interface TermsDetailModalProps {
  onDismiss: (result: boolean) => void
  termsId: number
  isScrollEvent?: boolean
  buttonProps?: ButtonProps & {
    footerContent?: string
  }
}

export function TermsDetailModal({ termsId, onDismiss, isScrollEvent, buttonProps }: TermsDetailModalProps) {
  const { isScrollBottom, setIsScrollBottom, checkDidMountScrollHeight, checkScrollToBottom } = useScrollToBottom()

  const {
    data: termsDetail,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.terms, termsId],
    queryFn: async () => await getTermsDetail(termsId),
    enabled: typeof termsId === 'number',
  })

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isPending || isError) return

    getScrollInfo()
  }, [isPending, isError])

  const getScrollInfo = async () => {
    if (!contentRef.current) return

    const { scrollHeight, clientHeight } = await contentRef.current
    if (!scrollHeight || !clientHeight) {
      setIsScrollBottom(true)
      return
    }

    checkDidMountScrollHeight(scrollHeight, clientHeight)
  }

  if (isPending || isError) {
    return (
      <ModalWrap>
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </ModalWrap>
    )
  }

  return (
    <ModalWrap>
      <Header type="MODAL" title="" onDismiss={() => onDismiss(false)} />
      <StyledContent ref={contentRef} onScroll={checkScrollToBottom}>
        <ArtipioContents>
          {termsDetail.contents?.map((content, index) => {
            const isFirstImg = content.type === 'image' && index === 0
            const isLastImg = content.type === 'image' && index === Number(termsDetail.contents?.length) - 1
            return <ArtipioContentItem key={index} {...content} isFirstImg={isFirstImg} isLastImg={isLastImg} />
          })}
        </ArtipioContents>
      </StyledContent>
      <Footer>
        <Button
          size="large"
          disabled={isScrollEvent && !isScrollBottom}
          block={true}
          onClick={() => onDismiss(true)}
          {...buttonProps}
        >
          {buttonProps?.footerContent || '확인'}
        </Button>
      </Footer>
    </ModalWrap>
  )
}

const StyledContent = styled(Content)`
  padding-bottom: 71px;
`
